import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTeam } from '../team/TeamContext';

/**
 * Route guard — redirects to /login if user is not authenticated.
 * In local mode (no Supabase), all routes are accessible.
 */
export function AuthGuard({ children }) {
  const { isAuthenticated, loading, isLocalMode } = useAuth();
  const location = useLocation();

  // Local mode: no auth required
  if (isLocalMode) return children;

  // Still loading auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  // Not authenticated: redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * Legacy admin-only route guard (checks profiles.role).
 */
export function AdminGuard({ children }) {
  const { isAdmin, isLocalMode } = useAuth();

  if (isLocalMode) return children;

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Team admin route guard — checks if user is admin of current team.
 */
export function TeamAdminGuard({ children }) {
  const { isLocalMode } = useAuth();
  const { isTeamAdmin, currentTeam, loading } = useTeam();

  if (isLocalMode) return children;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!currentTeam || !isTeamAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}
